/* MADDOG by M/D Atelier — vanilla JS
   - Homepage header: transparent at top, matte black on scroll
   - Homepage intro choreography (once per session)
   - Fade-in + mask-reveal + hairline + glacial image bleed via IntersectionObserver
   - Magnetic primary CTA (desktop only, ~3px max)
   - Respects prefers-reduced-motion */

(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isHome = document.body.getAttribute('data-template') === 'index';
  const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;

  /* -----------------------------------------------------------
     Header scroll behavior (homepage only)
     ----------------------------------------------------------- */
  function getHeaderEl() {
    return (
      document.querySelector('sticky-header') ||
      document.querySelector('.section-header') ||
      document.querySelector('.shopify-section-header-group-header-group') ||
      document.querySelector('[class*="shopify-section-group-header-group"]') ||
      document.querySelector('.shopify-section-header')
    );
  }

  function applyHeaderState(headerEl) {
    if (!headerEl) return;
    if (!isHome) {
      headerEl.classList.remove('maddog-header-transparent');
      return;
    }
    if (window.scrollY < 80) {
      headerEl.classList.add('maddog-header-transparent');
    } else {
      headerEl.classList.remove('maddog-header-transparent');
    }
  }

  function initHeader() {
    const headerEl = getHeaderEl();
    if (!headerEl) return;

    applyHeaderState(headerEl);

    let ticking = false;
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            applyHeaderState(headerEl);
            ticking = false;
          });
          ticking = true;
        }
      },
      { passive: true }
    );
  }

  /* -----------------------------------------------------------
     Homepage intro choreography
     Runs once per session on the homepage hero (the first
     image-banner in the main content). Staged via CSS using
     data-maddog-intro states on the body.
     ----------------------------------------------------------- */
  const INTRO_KEY = 'maddog-intro-played-v1';

  function shouldRunIntro() {
    if (!isHome) return false;
    if (reduceMotion) return false;
    try {
      if (sessionStorage.getItem(INTRO_KEY) === '1') return false;
    } catch (e) {
      /* sessionStorage may be unavailable — fail open and play once */
    }
    return true;
  }

  function markIntroPlayed() {
    try {
      sessionStorage.setItem(INTRO_KEY, '1');
    } catch (e) { /* noop */ }
  }

  function initIntro() {
    if (!shouldRunIntro()) {
      document.body.setAttribute('data-maddog-intro', 'done');
      return;
    }

    document.body.setAttribute('data-maddog-intro', 'active');

    // Allow the browser to paint the initial state, then switch to "playing"
    // which triggers the staged CSS transitions.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document.body.setAttribute('data-maddog-intro', 'playing');
      });
    });

    // After the choreography completes, mark as done and persist for the session.
    window.setTimeout(() => {
      document.body.setAttribute('data-maddog-intro', 'done');
      markIntroPlayed();
    }, 2400);
  }

  /* -----------------------------------------------------------
     Reveal — Phase IV.6C: cloak retired.

     Previously this function auto-tagged every top-level section
     with [data-maddog-reveal] which set opacity: 0 in CSS and
     waited for an IntersectionObserver (threshold 0.08) to fire
     before revealing. On the Vault collection page the
     progressive-grid section is thousands of pixels tall — a
     fresh page load never had 8% of it intersecting the viewport,
     so the section stayed at opacity: 0 and the entire page
     appeared black. Filter clicks (ALL / M / F / U) reproduced
     this on every navigation.

     Fix: immediately mark every reveal target `.is-visible` on
     DOMContentLoaded. Any element that opts in via Liquid class
     or attribute renders visible from the first paint. The
     homepage `initIntro()` Pause choreography (driven by
     data-maddog-intro states, not .is-visible) is unaffected.
     ----------------------------------------------------------- */
  function initReveal() {
    document
      .querySelectorAll(
        '[data-maddog-reveal], .maddog-reveal, .maddog-mask-reveal, .maddog-hairline, .maddog-bleed'
      )
      .forEach((el) => el.classList.add('is-visible'));
  }

  /* -----------------------------------------------------------
     Magnetic primary CTA — desktop only, ~3px max displacement
     Tags only the hero button on the homepage; one displacement
     per pointer position, smoothed by RAF.
     ----------------------------------------------------------- */
  function initMagnetic() {
    if (reduceMotion || isCoarsePointer) return;
    if (!isHome) return;

    // Hero CTA only — strictly one magnetic surface per page.
    const heroBtn = document.querySelector(
      'main .shopify-section:first-of-type .banner__buttons .button'
    );
    if (!heroBtn) return;

    heroBtn.classList.add('maddog-magnetic');

    const RADIUS = 60;
    const MAX_SHIFT = 3;
    let pending = false;
    let lastEvent = null;

    function update() {
      pending = false;
      if (!lastEvent) return;
      const rect = heroBtn.getBoundingClientRect();
      if (rect.width === 0) return;
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = lastEvent.clientX - cx;
      const dy = lastEvent.clientY - cy;
      const dist = Math.hypot(dx, dy);
      if (dist < RADIUS) {
        const factor = (RADIUS - dist) / RADIUS;
        const tx = (dx / RADIUS) * MAX_SHIFT * factor;
        const ty = (dy / RADIUS) * MAX_SHIFT * factor;
        heroBtn.style.transform = `translate(${tx.toFixed(2)}px, ${ty.toFixed(2)}px)`;
      } else if (heroBtn.style.transform) {
        heroBtn.style.transform = '';
      }
    }

    document.addEventListener(
      'pointermove',
      (e) => {
        lastEvent = e;
        if (!pending) {
          pending = true;
          window.requestAnimationFrame(update);
        }
      },
      { passive: true }
    );

    document.addEventListener('pointerleave', () => {
      lastEvent = null;
      heroBtn.style.transform = '';
    });
  }

  /* -----------------------------------------------------------
     Sticky mobile CTA (product page)
     Mirrors the main add-to-cart button. Shows when the main
     button scrolls out of view. CSS hides the element on desktop.
     ----------------------------------------------------------- */
  function initStickyCta() {
    const sticky = document.querySelector('[data-maddog-sticky-cta]');
    if (!sticky) return;

    const stickyBtn = sticky.querySelector('[data-maddog-sticky-trigger]');
    const stickyLabel = stickyBtn ? stickyBtn.querySelector('span') : null;
    const mainBtn = document.querySelector(
      'product-info product-form .product-form__submit, product-info .product-form__submit'
    );

    if (!stickyBtn || !mainBtn) return;

    function syncState() {
      stickyBtn.disabled = mainBtn.disabled;
      // Find the visible label span on the main button (Dawn uses a span for the label;
      // sold-out / unavailable / spinner are siblings).
      const candidates = mainBtn.querySelectorAll('span');
      let labelText = '';
      for (const node of candidates) {
        const cls = node.className || '';
        if (cls.indexOf('loading') !== -1 || cls.indexOf('sold-out-message') !== -1) continue;
        const t = (node.textContent || '').trim();
        if (t) { labelText = t; break; }
      }
      if (labelText && stickyLabel) stickyLabel.textContent = labelText;
    }

    syncState();

    if ('MutationObserver' in window) {
      const mo = new MutationObserver(syncState);
      mo.observe(mainBtn, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    stickyBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (mainBtn.disabled) return;
      // Delegate to the main form's submit button — Shopify's product-form
      // custom element handles AJAX add-to-cart + cart drawer open.
      mainBtn.click();
    });

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            sticky.classList.toggle('is-visible', !entry.isIntersecting);
          });
        },
        { rootMargin: '0px 0px 0px 0px', threshold: 0 }
      );
      io.observe(mainBtn);
    } else {
      // No IO support — show always on mobile.
      sticky.classList.add('is-visible');
    }
  }

  /* -----------------------------------------------------------
     Atlas (Phase 4) — curated emotional landscape
     Each node is a focusable link. Hover/focus reveals an
     editorial card positioned inside the canvas bounds.
     No autoplay, no animation theatre.
     ----------------------------------------------------------- */
  function initAtlas() {
    document.querySelectorAll('.maddog-atlas').forEach((atlas) => {
      const canvas = atlas.querySelector('.maddog-atlas__canvas');
      if (!canvas) return;

      const nodes = canvas.querySelectorAll('.maddog-atlas__node[aria-describedby]');
      if (!nodes.length) return;

      nodes.forEach((node) => {
        const cardId = node.getAttribute('aria-describedby');
        const card = cardId ? canvas.querySelector('#' + cardId) : null;
        if (!card) return;

        let hideTimer = null;

        function positionCard() {
          const canvasRect = canvas.getBoundingClientRect();
          const nodeRect = node.getBoundingClientRect();
          // Card dimensions — measure live in case content changed.
          const cardWidth = card.offsetWidth || 220;
          const cardHeight = card.offsetHeight || 320;
          const gap = 16;

          const nodeXInCanvas = nodeRect.left - canvasRect.left;
          const nodeYInCanvas = nodeRect.top - canvasRect.top;

          let cardLeft = nodeXInCanvas + nodeRect.width + gap;
          if (cardLeft + cardWidth > canvasRect.width - gap) {
            // Place to the left
            cardLeft = nodeXInCanvas - cardWidth - gap;
          }
          if (cardLeft < gap) cardLeft = gap;

          let cardTop = nodeYInCanvas - cardHeight / 2 + nodeRect.height / 2;
          if (cardTop < gap) cardTop = gap;
          if (cardTop + cardHeight > canvasRect.height - gap) {
            cardTop = canvasRect.height - cardHeight - gap;
          }

          card.style.left = cardLeft + 'px';
          card.style.top = cardTop + 'px';
        }

        function show() {
          if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
          // Hide other active cards in this atlas (one at a time).
          atlas.querySelectorAll('.maddog-atlas__card.is-active').forEach((c) => {
            if (c !== card) c.classList.remove('is-active');
          });
          positionCard();
          card.classList.add('is-active');
        }

        function scheduleHide() {
          if (hideTimer) clearTimeout(hideTimer);
          hideTimer = window.setTimeout(() => {
            card.classList.remove('is-active');
          }, 140);
        }

        node.addEventListener('pointerenter', show);
        node.addEventListener('focus', show);
        node.addEventListener('pointerleave', scheduleHide);
        node.addEventListener('blur', scheduleHide);

        // Hover-bridge — entering the card itself keeps it visible.
        card.addEventListener('pointerenter', show);
        card.addEventListener('pointerleave', scheduleHide);

        // Dismiss on Escape while focused
        node.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            card.classList.remove('is-active');
            node.blur();
          }
        });
      });
    });
  }

  /* -----------------------------------------------------------
     MD ATELIER — pre-launch coming-soon overlay.
     Intercepts clicks on the specific MADDOG main-nav triggers
     (#HeaderMenu-md-atelier on desktop, #HeaderDrawer-md-atelier
     on mobile) and opens the native <dialog> instead of navigating
     to /pages/atelier. Every other /pages/atelier link on the
     storefront still works — we do not global-hijack the URL.
     ----------------------------------------------------------- */
  function initMdAtelierOverlay() {
    const overlay = document.querySelector('[data-md-atelier-overlay]');
    if (!overlay) return;

    // Header main-nav triggers (Dawn-generated IDs from link.handle)
    // + footer-scoped MD ATELIER trigger. The footer link is a stock
    // Dawn link_list link (no data attribute), so we scope by container
    // .footer + href suffix. Any other /pages/atelier link elsewhere on
    // the storefront is unaffected — no global URL hijack.
    let triggers = document.querySelectorAll(
      '#HeaderMenu-md-atelier, #HeaderDrawer-md-atelier, .footer a[href$="/pages/atelier"]'
    );
    if (!triggers.length) {
      // Handle-suffix fallback in case Shopify assigned a numeric suffix.
      triggers = document.querySelectorAll(
        'a[id^="HeaderMenu-md-atelier"], a[id^="HeaderDrawer-md-atelier"], .footer a[href$="/pages/atelier"]'
      );
    }
    if (!triggers.length) return;

    const closeBtn = overlay.querySelector('[data-md-atelier-close]');

    function lockScroll() {
      document.body.classList.add('md-atelier-overlay-open');
    }
    function unlockScroll() {
      document.body.classList.remove('md-atelier-overlay-open');
    }

    function openOverlay(evt) {
      if (evt) {
        evt.preventDefault();
        evt.stopPropagation();
      }
      // If the mobile drawer is open, close it so it isn't stacked
      // behind the overlay (and the drawer's inert state doesn't
      // interfere with focus once the dialog closes).
      const drawerDetails = document.querySelector(
        'header-drawer > details[open]'
      );
      if (drawerDetails) drawerDetails.removeAttribute('open');

      lockScroll();
      if (typeof overlay.showModal === 'function') {
        try {
          overlay.showModal();
        } catch (_) {
          overlay.setAttribute('open', '');
        }
      } else {
        overlay.setAttribute('open', '');
      }
    }

    function closeOverlay() {
      if (typeof overlay.close === 'function' && overlay.open) {
        overlay.close();
      } else {
        overlay.removeAttribute('open');
      }
      unlockScroll();
    }

    triggers.forEach((t) => t.addEventListener('click', openOverlay));

    if (closeBtn) closeBtn.addEventListener('click', closeOverlay);

    // Backdrop click: clicking directly on the dialog element (not its
    // inner content) means the click hit the backdrop area of the
    // full-viewport dialog.
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) closeOverlay();
    });

    // Native Escape close on <dialog> fires the 'cancel' event; the
    // dialog may also close programmatically firing 'close'. Both
    // must clean up the body scroll lock.
    overlay.addEventListener('close', unlockScroll);
    overlay.addEventListener('cancel', unlockScroll);
  }

  /* -----------------------------------------------------------
     RITUAL — intro CTA temporary reveal.
     The dedicated custom-blend builder does not exist yet, so
     the CREATE YOUR BLEND CTA on /pages/ritual toggles a small
     inline "coming soon" message rather than navigating anywhere.
     Fully additive — has no effect on pages that do not render
     the [data-ritual-builder-trigger] button.
     ----------------------------------------------------------- */
  function initRitualIntro() {
    document
      .querySelectorAll('[data-ritual-builder-trigger]')
      .forEach((btn) => {
        const targetId = btn.getAttribute('aria-controls');
        const msg = targetId ? document.getElementById(targetId) : null;
        if (!msg) return;
        btn.addEventListener('click', () => {
          msg.hidden = false;
          msg.classList.add('is-visible');
          btn.setAttribute('aria-expanded', 'true');
          // Ensure screen readers announce the newly-revealed state
          // and keyboard users can perceive it without scroll jump.
          if (!msg.hasAttribute('tabindex')) {
            msg.setAttribute('tabindex', '-1');
          }
          try {
            msg.focus({ preventScroll: true });
          } catch (_) {
            msg.focus();
          }
        });
      });
  }

  /* -----------------------------------------------------------
     Init
     ----------------------------------------------------------- */
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(() => {
    initIntro();
    initHeader();
    initReveal();
    initMagnetic();
    initStickyCta();
    initAtlas();
    initMdAtelierOverlay();
    initRitualIntro();
  });
})();
